import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../api';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const DetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const BackButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  cursor: pointer;
  
  &:hover {
    background: #5a6268;
  }
`;

const HeaderSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 20px;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  
  > div {
    flex: 1;
  }
`;

const MetaLabel = styled.span`
  color: #666;
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
`;

const MetaValue = styled.span`
  color: #333;
  font-size: 16px;
  font-weight: 500;
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: #0000FF;
  margin-bottom: 20px;
  font-size: 1.5rem;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  margin-bottom: 15px;
`;

const DataLabel = styled.div`
  font-weight: 600;
  color: #555;
`;

const DataValue = styled.div`
  color: #333;
  white-space: pre-wrap;
`;

const PieceCard = styled.div`
  background: #f8f9fa;
  padding: 25px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
`;

const PieceTitle = styled.h3`
  color: #333;
  margin-bottom: 20px;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const MediaCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 150px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MediaInfo = styled.div`
  padding: 10px;
  font-size: 12px;
  
  .filename {
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .size {
    color: #666;
    margin-top: 5px;
  }
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
`;

const StatusSelect = styled.select`
  padding: 8px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const UpdateButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #218838;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const FormSubmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.getFormSubmissionById(id);
      setSubmission(response.data);
      setStatus(response.data.status);
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError('Erreur lors du chargement de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await api.updateFormSubmissionStatus(id, status);
      alert('Statut mis à jour avec succès');
      fetchSubmission();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Nouveau';
      case 'reviewed': return 'Examiné';
      case 'approved': return 'Approuvé';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const handleMediaClick = (mediaUrl) => {
    window.open(mediaUrl, '_blank');
  };

  if (loading) {
    return (
      <DetailContainer>
        <LoadingSpinner>Chargement de la soumission...</LoadingSpinner>
      </DetailContainer>
    );
  }

  if (error || !submission) {
    return (
      <DetailContainer>
        <p style={{ color: 'red' }}>{error || 'Soumission non trouvée'}</p>
        <BackButton onClick={() => navigate('/admin/events')}>
          ← Retour à la liste
        </BackButton>
      </DetailContainer>
    );
  }

  const { data } = submission;
  const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;

  return (
    <DetailContainer>
      <BackButton onClick={() => navigate('/admin/events')}>
        ← Retour à la liste
      </BackButton>

      <HeaderSection>
        <Title>Candidature Milan 2026 - {data.userType === 'designer' ? data.designerCompanyName : data.fabricantCompanyName}</Title>
        <MetaInfo>
          <div>
            <MetaLabel>Date de soumission</MetaLabel>
            <MetaValue>{dayjs(submission.createdAt).format('DD MMMM YYYY à HH:mm')}</MetaValue>
          </div>
          <div>
            <MetaLabel>ID de soumission</MetaLabel>
            <MetaValue>{submission._id}</MetaValue>
          </div>
          <div>
            <MetaLabel>Statut actuel</MetaLabel>
            <MetaValue>{getStatusText(submission.status)}</MetaValue>
          </div>
        </MetaInfo>
        
        <StatusSection>
          <label>Modifier le statut:</label>
          <StatusSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="pending">Nouveau</option>
            <option value="reviewed">Examiné</option>
            <option value="approved">Approuvé</option>
            <option value="rejected">Rejeté</option>
          </StatusSelect>
          <UpdateButton onClick={handleStatusUpdate}>
            Mettre à jour
          </UpdateButton>
        </StatusSection>
      </HeaderSection>

      <Section>
        <SectionTitle>Contact du candidat</SectionTitle>
        <DataGrid>
          <DataLabel>Prénom:</DataLabel>
          <DataValue>{data.firstName}</DataValue>
          
          <DataLabel>Nom:</DataLabel>
          <DataValue>{data.lastName}</DataValue>
          
          <DataLabel>Email:</DataLabel>
          <DataValue>{data.email}</DataValue>
          
          <DataLabel>Téléphone:</DataLabel>
          <DataValue>{data.phoneNumber}</DataValue>
          
          <DataLabel>Type d'utilisateur:</DataLabel>
          <DataValue>{data.userType === 'designer' ? 'Designer' : 'Fabricant/Éditeur'}</DataValue>
        </DataGrid>
      </Section>

      {data.userType === 'designer' ? (
        <Section>
          <SectionTitle>Informations du designer</SectionTitle>
          <DataGrid>
            <DataLabel>Nom de l'entreprise:</DataLabel>
            <DataValue>{data.designerCompanyName}</DataValue>
            
            <DataLabel>L'entreprise est-elle présente à l'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?</DataLabel>
            <DataValue>{data.designerInternationalPresence}</DataValue>
            
            {data.designerInternationalDetails && (
              <>
                <DataLabel>Détails présence internationale:</DataLabel>
                <DataValue>{data.designerInternationalDetails}</DataValue>
              </>
            )}
            
            <DataLabel>L'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?</DataLabel>
            <DataValue>{data.designerWebAndSocial}</DataValue>
            
            {data.designerWebAndSocialDetails && (
              <>
                <DataLabel>Détails web et réseaux sociaux:</DataLabel>
                <DataValue>{data.designerWebAndSocialDetails}</DataValue>
              </>
            )}
          </DataGrid>
          
          <DataGrid style={{ marginTop: '20px' }}>
            <DataLabel>Présentation:</DataLabel>
            <DataValue>{data.designerPresentation}</DataValue>
          </DataGrid>
        </Section>
      ) : (
        <Section>
          <SectionTitle>Informations du fabricant/éditeur</SectionTitle>
          <DataGrid>
            <DataLabel>Nom de l'entreprise:</DataLabel>
            <DataValue>{data.fabricantCompanyName}</DataValue>
            
            <DataLabel>L'entreprise est-elle cotisante au CODIFAB ?</DataLabel>
            <DataValue>{data.fabricantCodifab}</DataValue>
            
            <DataLabel>L'entreprise est-elle adhérente à l'Ameublement français ?</DataLabel>
            <DataValue>{data.fabricantAmeublement}</DataValue>
            
            <DataLabel>L'entreprise est-elle présente à l'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?</DataLabel>
            <DataValue>{data.fabricantInternationalPresence}</DataValue>
            
            {data.fabricantInternationalDetails && (
              <>
                <DataLabel>Détails présence internationale:</DataLabel>
                <DataValue>{data.fabricantInternationalDetails}</DataValue>
              </>
            )}
            
            <DataLabel>L'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?</DataLabel>
            <DataValue>{data.fabricantWebAndSocial}</DataValue>
            
            {data.fabricantWebAndSocialDetails && (
              <>
                <DataLabel>Détails web et réseaux sociaux:</DataLabel>
                <DataValue>{data.fabricantWebAndSocialDetails}</DataValue>
              </>
            )}
          </DataGrid>
          
          <DataGrid style={{ marginTop: '20px' }}>
            <DataLabel>Présentation:</DataLabel>
            <DataValue>{data.fabricantPresentation}</DataValue>
          </DataGrid>
        </Section>
      )}

      {data.certificationAccepted && (
        <Section>
          <SectionTitle>Certification</SectionTitle>
          <DataValue>✓ Le candidat a certifié que les informations fournies sont exactes et véridiques.</DataValue>
        </Section>
      )}

      <Section>
        <SectionTitle>Pièces proposées ({data.pieces?.length || 0})</SectionTitle>
        
        {data.pieces?.map((piece, index) => (
          <PieceCard key={index}>
            <PieceTitle>Pièce {index + 1}: {piece.name}</PieceTitle>
            
            <DataGrid>
              <DataLabel>Typologie:</DataLabel>
              <DataValue>{piece.typologie === 'autre' && piece.typologieOther ? piece.typologieOther : piece.typologie}</DataValue>
              
              <DataLabel>Précisez la date de création de la pièce (maximum 2 ans d'ancienneté soit à partir d'avril 2024). Merci de préciser si la pièce est en cours de création.</DataLabel>
              <DataValue>{piece.creationDate}</DataValue>
              
              {/* Designer information for piece */}
              {data.userType === 'designer' ? (
                <>
                  <DataLabel>Designer:</DataLabel>
                  <DataValue>{data.designerCompanyName}</DataValue>
                </>
              ) : piece.designerCompanyName && (
                <>
                  <DataLabel>Designer - Nom de l'entreprise:</DataLabel>
                  <DataValue>{piece.designerCompanyName}</DataValue>
                  
                  {piece.designerInternationalPresence && (
                    <>
                      <DataLabel>Designer - L'entreprise est-elle présente à l'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?</DataLabel>
                      <DataValue>{piece.designerInternationalPresence}</DataValue>
                    </>
                  )}
                  
                  {piece.designerInternationalDetails && (
                    <>
                      <DataLabel>Designer - Détails international:</DataLabel>
                      <DataValue>{piece.designerInternationalDetails}</DataValue>
                    </>
                  )}
                  
                  {piece.designerWebAndSocial && (
                    <>
                      <DataLabel>Designer - L'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?</DataLabel>
                      <DataValue>{piece.designerWebAndSocial}</DataValue>
                    </>
                  )}
                  
                  {piece.designerWebAndSocialDetails && (
                    <>
                      <DataLabel>Designer - Détails web/social:</DataLabel>
                      <DataValue>{piece.designerWebAndSocialDetails}</DataValue>
                    </>
                  )}
                  
                  {piece.designerPresentation && (
                    <>
                      <DataLabel>Designer - Présentation:</DataLabel>
                      <DataValue>{piece.designerPresentation}</DataValue>
                    </>
                  )}
                </>
              )}
              
              {/* Fabricant information for piece */}
              {data.userType === 'fabricant' ? (
                <>
                  <DataLabel>Fabricant/Éditeur:</DataLabel>
                  <DataValue>{data.fabricantCompanyName}</DataValue>
                </>
              ) : piece.fabricantCompanyName && (
                <>
                  <DataLabel>Fabricant - Nom de l'entreprise:</DataLabel>
                  <DataValue>{piece.fabricantCompanyName}</DataValue>
                  
                  {piece.fabricantCodifab && (
                    <>
                      <DataLabel>Fabricant - L'entreprise est-elle cotisante au CODIFAB ?</DataLabel>
                      <DataValue>{piece.fabricantCodifab}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantAmeublement && (
                    <>
                      <DataLabel>Fabricant - L'entreprise est-elle adhérente à l'Ameublement français ?</DataLabel>
                      <DataValue>{piece.fabricantAmeublement}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantInternationalPresence && (
                    <>
                      <DataLabel>Fabricant - L'entreprise est-elle présente à l'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?</DataLabel>
                      <DataValue>{piece.fabricantInternationalPresence}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantInternationalDetails && (
                    <>
                      <DataLabel>Fabricant - Détails international:</DataLabel>
                      <DataValue>{piece.fabricantInternationalDetails}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantWebAndSocial && (
                    <>
                      <DataLabel>Fabricant - L'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?</DataLabel>
                      <DataValue>{piece.fabricantWebAndSocial}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantWebAndSocialDetails && (
                    <>
                      <DataLabel>Fabricant - Détails web/social:</DataLabel>
                      <DataValue>{piece.fabricantWebAndSocialDetails}</DataValue>
                    </>
                  )}
                  
                  {piece.fabricantPresentation && (
                    <>
                      <DataLabel>Fabricant - Présentation:</DataLabel>
                      <DataValue>{piece.fabricantPresentation}</DataValue>
                    </>
                  )}
                </>
              )}
              
              <DataLabel>Précisez le savoir-faire de la pièce</DataLabel>
              <DataValue>{piece.savoirFaire}</DataValue>
              
              <DataLabel>Précisez en quoi votre pièce s'inscrit dans une démarche de durabilité (écologique, éthique, qualitative)</DataLabel>
              <DataValue>{piece.durability}</DataValue>
              
              <DataLabel>Texte de présentation de la pièce</DataLabel>
              <DataValue style={{ whiteSpace: 'pre-wrap' }}>{piece.presentationText}</DataValue>
              
              <DataLabel>Dimensions</DataLabel>
              <DataValue>{piece.dimensions}</DataValue>
              
              <DataLabel>Matériaux</DataLabel>
              <DataValue>{piece.materials}</DataValue>
              
              {piece.poids && (
                <>
                  <DataLabel>Poids:</DataLabel>
                  <DataValue>{piece.poids}</DataValue>
                </>
              )}
            </DataGrid>
            
            {piece.uploadedMedia && piece.uploadedMedia.length > 0 && (
              <>
                <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>
                  Médias uploadés ({piece.uploadedMedia.length})
                </h4>
                <MediaGrid>
                  {piece.uploadedMedia.map((media, idx) => (
                    <MediaCard 
                      key={idx}
                      onClick={() => handleMediaClick(`${mediaBaseUrl}/${media.path}`)}
                    >
                      <MediaPreview>
                        {media.mimetype.startsWith('image/') ? (
                          <img 
                            src={`${mediaBaseUrl}/${media.path}`} 
                            alt={media.originalname}
                          />
                        ) : media.mimetype.startsWith('video/') ? (
                          <div>🎥 Vidéo</div>
                        ) : (
                          <div>📄 PDF</div>
                        )}
                      </MediaPreview>
                      <MediaInfo>
                        <div className="filename">{media.originalname}</div>
                        <div className="size">{(media.size / 1024 / 1024).toFixed(2)} MB</div>
                        {media.credit && (
                          <div className="size" style={{ marginTop: '3px', fontStyle: 'italic' }}>
                            {media.credit}
                          </div>
                        )}
                      </MediaInfo>
                    </MediaCard>
                  ))}
                </MediaGrid>
              </>
            )}
          </PieceCard>
        ))}
      </Section>
    </DetailContainer>
  );
};

export default FormSubmissionDetail;